import { AccountType, DocumentType, Employee, Position, Tag, getEmployeeAccessTranslationId } from '@synergy/models';
import { Box, Stack, Theme, useTheme } from '@mui/material';
import { FormattedDate, FormattedMessage, useIntl } from 'react-intl';
import { Localized, PoolButton, PoolChip, ValueOrNDash } from '@synergy/components';
import { LockIcon, PlusCircleIcon } from '@synergy/theme';
import {
    PaloColumn,
    PaloListView,
    PaloLockedSwitch,
    PaloRolePills,
    PaloRowAction,
    PaloStatusPill,
} from '@synergy/components-palo';
import { PoolFilterSearchBar, PoolLink, PoolTypography } from '@synergy/components-next';
import { makeStyles } from '@mui/styles';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';

import * as ActivityActions from '../../../../../common/actions/activity-actions';
import * as CustomerEmployeeActions from './store/customer-employee-actions';
import { AddEmployeeModal } from '../../../components/employee/add-employee-modal';
import { ChangeEmployeeTagsModal } from '../../../components/employee/change-employee-tags-modal';
import { ChangePositionModal } from '../../../components/employee/change-position-modal';
import { EmployeeRoles } from './components/employee-roles';
import { ROUTES } from '../../../../sidebar-menu/navigation-drawer/routes';
import { RemoveEmployeeModal } from './components/remove-employee-modal';
import { byMemberEmployeeName, memberEmployeeName } from '../../../utils/employee-name';
import { customerBonusContactCountSelector, customerEmployeeSelector } from './store/customer-employee-selectors';
import { filterEmployees } from '../../../utils/filter-employees';
import { useAppSelector } from '../../../../../store/use-appstate-selector';
import { useThunkDispatch } from '../../../../../store/use-thunk-dispatch';

const useStyles = makeStyles((theme: Theme) => ({
    table: {
        backgroundColor: 'white',
        '& tbody tr:last-child td': {
            paddingRight: 24,
        },
        marginBottom: theme.spacing(2),
    },
    chip: {
        marginLeft: 0,
        marginRight: theme.spacing(),
        marginTop: theme.spacing(0.5),
        marginBottom: theme.spacing(0.5),
        backgroundColor: '#F2F2F2',
    },
    dash: {
        marginLeft: theme.spacing(),
        color: theme.palette.grey[700],
    },
    addButton: {
        fontSize: 20,
    },
    disabledRow: {
        opacity: 0.5,
    },
}));

export const CustomerEmployees = () => {
    const classes = useStyles();
    const theme = useTheme();
    const { id: customerIdParam } = useParams<{ id: string }>();
    const [removeModalData, setRemoveModalData] = useState<Employee | null>(null);
    const [showAddModal, setShowAddModal] = useState<boolean>(false);
    const [changePositionModalData, setChangePositionModalData] = useState<Employee | null>(null);
    const [tagsModalData, setTagsModalData] = useState<Employee | null>(null);

    const navigate = useNavigate();
    const locales = useAppSelector(state => state.supportedLocales);
    const intl = useIntl();
    const customerEmployees = useAppSelector(customerEmployeeSelector);
    const dispatch = useThunkDispatch();
    const bonusContactCount = useAppSelector(customerBonusContactCountSelector);
    const [employees, setEmployees] = useState<Employee[] | null>(customerEmployees);
    const [search, setSearch] = useState('');
    /*
     * Someone who has left is out of the way until asked for, as in every
     * other master data list (LST-S14; the tab was left to follow with
     * #2221).
     */
    const [showLocked, setShowLocked] = useState(false);

    useEffect(() => {
        if (customerIdParam) {
            dispatch(CustomerEmployeeActions.fetchCustomerEmployees(+customerIdParam));
        }
    }, [customerIdParam, dispatch]);

    useEffect(() => {
        setEmployees(filterEmployees(customerEmployees, search, locales));
    }, [customerEmployees, search, locales]);

    if (!employees) {
        return null;
    }

    const handleRemove = async (
        reassignMainContactId?: number,
        reassignTopicsContactId?: number,
        reassignBonusContactId?: number
    ) => {
        if (customerIdParam && removeModalData?.id) {
            await dispatch(
                CustomerEmployeeActions.removeEmployee(
                    +customerIdParam,
                    removeModalData?.id,
                    reassignMainContactId,
                    reassignTopicsContactId,
                    reassignBonusContactId
                )
            );
            dispatch(ActivityActions.fetchActivities(DocumentType.Customer, +customerIdParam));
        }
        setRemoveModalData(null);
    };

    const handleAdd = async (newEmployee: Employee) => {
        if (customerIdParam) {
            await dispatch(
                CustomerEmployeeActions.addEmployee(+customerIdParam, {
                    ...newEmployee,
                    isMainContact: !employees.some(employee => employee.isMainContact),
                })
            );
            dispatch(ActivityActions.fetchActivities(DocumentType.Customer, +customerIdParam));
        }
        setShowAddModal(false);
    };

    const handleSetMainContact = (employeeId: number) => {
        if (customerIdParam) {
            dispatch(CustomerEmployeeActions.setEmployeeAsMainContact(+customerIdParam, employeeId));
        }
    };

    const handleSetBonusContact = (employeeId: number, isBonusContact: boolean) => {
        if (customerIdParam) {
            dispatch(
                CustomerEmployeeActions.updateEmployeeAsBonusContact(+customerIdParam, employeeId, isBonusContact)
            );
        }
    };

    const handleEdit = (employeeId: number) => {
        navigate(ROUTES.contactsAndOrganizations.contacts.detail.replace(':id', employeeId.toString()));
    };

    const handleChangePosition = (employee: Employee | null, position: Position | null) => {
        if (employee && customerIdParam) {
            dispatch(CustomerEmployeeActions.changeEmployeePosition(+customerIdParam, employee.id, position));
        }
        setChangePositionModalData(null);
    };

    const handleChangeTags = (employee: Employee | null, tags: Tag[]) => {
        if (employee && customerIdParam) {
            dispatch(CustomerEmployeeActions.changeEmployeeTags(+customerIdParam, employee.id, tags));
        }
        setTagsModalData(null);
    };

    // The same order the row menu had. A locked person keeps only the entries
    // that still mean something, and the last bonus contact cannot be dropped.
    const employeeActions = (employee: Employee): PaloRowAction[] => [
        ...(employee.isLocked || employee.isMainContact
            ? []
            : [
                  {
                      label: <FormattedMessage id="app.contactsAndOrganizations.detail.mainContact" />,
                      onClick: () => handleSetMainContact(employee.id),
                  },
              ]),
        ...(employee.isLocked || employee.isBonusContact
            ? []
            : [
                  {
                      label: <FormattedMessage id="app.contactsAndOrganizations.detail.bonusContact" />,
                      onClick: () => handleSetBonusContact(employee.id, true),
                  },
              ]),
        ...(!employee.isLocked && employee.isBonusContact && 1 !== bonusContactCount
            ? [
                  {
                      label: <FormattedMessage id="app.contactsAndOrganizations.detail.removeBonusContact" />,
                      onClick: () => handleSetBonusContact(employee.id, false),
                  },
              ]
            : []),
        ...(employee.isLocked
            ? []
            : [
                  {
                      label: employee.position ? (
                          <FormattedMessage id="app.contactsAndOrganizations.detail.changePosition" />
                      ) : (
                          <FormattedMessage id="app.contactsAndOrganizations.detail.addPosition" />
                      ),
                      onClick: () => setChangePositionModalData(employee),
                  },
              ]),
        {
            label: <FormattedMessage id="app.contactsAndOrganizations.detail.editPerson" />,
            onClick: () => handleEdit(employee.id),
        },
        {
            label: <FormattedMessage id="app.contactsAndOrganizations.detail.assignTags" />,
            onClick: () => setTagsModalData(employee),
        },
        {
            label: <FormattedMessage id="app.contactsAndOrganizations.detail.removePerson" />,
            onClick: () => setRemoveModalData(employee),
        },
    ];

    const columns: PaloColumn<Employee>[] = [
        {
            key: 'name',
            type: 'name',
            head: <FormattedMessage id="app.table.column.name" />,
            cell: employee => (
                <Stack direction="row" spacing={1} alignItems="center">
                    {employee.isLocked && <LockIcon style={{ fontSize: 16 }} />}
                    <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{memberEmployeeName(employee)}</Box>
                    <PaloRolePills
                        roles={[
                            employee.isMainContact && (
                                <FormattedMessage id="app.contactsAndOrganizations.detail.mainContact" />
                            ),
                            employee.isBonusContact && (
                                <FormattedMessage id="app.contactsAndOrganizations.detail.bonusContact" />
                            ),
                        ]}
                    />
                </Stack>
            ),
        },
        {
            // What the person does at their own house — not one of the
            // functions beside the name, so it gets a column (LST-S3).
            key: 'position',
            type: 'classification',
            head: <FormattedMessage id="app.label.position" />,
            cell: employee => (
                <ValueOrNDash>
                    <Localized value={employee.position?.name} />
                </ValueOrNDash>
            ),
        },
        {
            key: 'email',
            type: 'name',
            variant: 'muted',
            head: <FormattedMessage id="app.common.email" />,
            cell: employee => (
                <PoolLink
                    href={`mailto:${employee.email}`}
                    colorVariant="grey700"
                    hoverColor={theme.palette.primary.main}
                    onClick={e => e.stopPropagation()}
                >
                    {employee.email}
                </PoolLink>
            ),
        },
        {
            // Mobile if there is one, else the main number, picked by the
            // server; a click calls rather than opening the row (#2315).
            key: 'phone',
            type: 'phone',
            variant: 'muted',
            head: <FormattedMessage id="app.common.phone" />,
            cell: employee => (
                <ValueOrNDash>
                    {employee.phone && (
                        <PoolLink
                            href={`tel:${employee.phone.replace(/[^\d+]/g, '')}`}
                            colorVariant="grey700"
                            hoverColor={theme.palette.primary.main}
                            onClick={e => e.stopPropagation()}
                        >
                            {employee.phone}
                        </PoolLink>
                    )}
                </ValueOrNDash>
            ),
        },
        {
            // Read off the login record: whether there is one, whether the
            // invitation is still out, whether it is blocked. The sentence
            // "Kein Zugang vorhanden" used to sit in a cell and said nothing
            // about the other four cases (#2193).
            key: 'access',
            type: 'status',
            head: <FormattedMessage id="app.table.column.access" />,
            cell: employee =>
                employee.access ? (
                    <PaloStatusPill
                        status={employee.access}
                        label={<FormattedMessage id={getEmployeeAccessTranslationId(employee.access)} />}
                    />
                ) : null,
        },
        {
            key: 'roles',
            wrap: true,
            head: <FormattedMessage id="app.contactsAndOrganizations.detail.roles" />,
            cell: employee => <EmployeeRoles employee={employee} />,
        },
        {
            key: 'lastLogin',
            type: 'date',
            head: <FormattedMessage id="app.table.column.lastLogin" />,
            cell: employee =>
                employee.lastLogin ? (
                    <FormattedDate value={employee.lastLogin} day="2-digit" month="2-digit" year="numeric" />
                ) : null,
        },
        {
            key: 'tags',
            wrap: true,
            head: <FormattedMessage id="app.contactsAndOrganizations.detail.tags" />,
            cell: employee => (
                <ValueOrNDash dashProps={{ className: classes.dash }}>
                    {employee.tags?.map(tag => (
                        <PoolChip key={tag.id} className={classes.chip} label={tag.name} type="tag" />
                    ))}
                </ValueOrNDash>
            ),
        },
    ];

    /*
     * Someone who has left is still on the list, only out of play. Above them
     * all stands the main contact: this table has no sorting of its own, so
     * the order it opens in is the only one there is (LST-S3a).
     */
    const sorted = [...employees]
        .filter(employee => showLocked || !employee.isLocked)
        .sort((a, b) => {
            if (a.isLocked !== b.isLocked) {
                return a.isLocked ? 1 : -1;
            }

            if (a.isMainContact !== b.isMainContact) {
                return a.isMainContact ? -1 : 1;
            }

            return byMemberEmployeeName(a, b);
        });

    return (
        <>
            <Stack
                justifyContent="flex-end"
                alignItems="center"
                gap={2}
                flexDirection="row"
                sx={{ margin: theme.spacing(2, theme.general.pageContentSpacing, 0, theme.general.pageContentSpacing) }}
            >
                <PaloLockedSwitch checked={showLocked} onChange={setShowLocked} />
                <PoolFilterSearchBar
                    onChange={setSearch}
                    placeholder={intl.formatMessage({
                        id: 'app.common.searchPlaceholder',
                    })}
                    onKeyDown={e => e.stopPropagation()}
                    value={search}
                    sx={{ minWidth: 230, width: 230 }}
                    backgroundColor="transparent"
                />
            </Stack>

            <Box
                sx={{
                    margin: theme.spacing(2, theme.general.pageContentSpacing),
                }}
            >
                {sorted.length > 0 && (
                    <PaloListView<Employee, number>
                        embedded={true}
                        columns={columns}
                        items={sorted}
                        getRowId={employee => employee.id}
                        emptyLabel={<FormattedMessage id="app.contactsAndOrganizations.detail.addPerson" />}
                        isRowMuted={employee => employee.isLocked}
                        onRowClick={employee => handleEdit(employee.id)}
                        rowHref={employee =>
                            ROUTES.contactsAndOrganizations.contacts.detail.replace(':id', employee.id.toString())
                        }
                        rowActions={employeeActions}
                    />
                )}

                <PoolButton
                    styleType="thirdLevel"
                    startIcon={<PlusCircleIcon color="primary" className={classes.addButton} />}
                    onClick={() => setShowAddModal(true)}
                >
                    <FormattedMessage id="app.contactsAndOrganizations.detail.addPerson" />
                </PoolButton>

                <AddEmployeeModal
                    isVisible={showAddModal}
                    organizationId={+(customerIdParam || 0)}
                    employeeType={AccountType.Customer}
                    onCancel={() => setShowAddModal(false)}
                    onConfirm={handleAdd}
                />
                <ChangePositionModal
                    employeeType={AccountType.Customer}
                    isVisible={!!changePositionModalData}
                    position={changePositionModalData?.position}
                    onCancel={() => setChangePositionModalData(null)}
                    onConfirm={position => handleChangePosition(changePositionModalData, position)}
                />
                <ChangeEmployeeTagsModal
                    isVisible={!!tagsModalData}
                    tags={tagsModalData?.tags}
                    onCancel={() => setTagsModalData(null)}
                    onConfirm={tags => handleChangeTags(tagsModalData, tags)}
                />
                <RemoveEmployeeModal
                    isVisible={!!removeModalData}
                    employees={employees}
                    employee={removeModalData}
                    bonusContactCount={bonusContactCount}
                    onCancel={() => setRemoveModalData(null)}
                    onConfirm={handleRemove}
                />
            </Box>
        </>
    );
};
